import { FILESCACHE_KEYS } from '@/shared/plugins/files-cache/enums/files-cache.enum';
import Vue from 'vue';
import { BUILDER_EVENT_TYPES, BUILDER_STATUS, BUILDER_SYNC_STATUS, COURSE_API } from '../values/courses.values';
import BuilderQueue from '@/courses/queues/builder-queue'
import { ContentStructureRequest, StructureRequest, StructureSections, SyncBuilderDataRequest } from '@/app/private/subjects/models/sync-builder-request.model';
import {RequestProvider} from '@/shared/providers/request.provider';
import { SyncBuilderDataResponse } from '@/app/private/subjects/models/sync-builder-response.model';
import { BuilderCachePlugin } from '@/shared/plugins/files-cache/builder-cache.plugin';
import { BuilderData } from '@/app/private/subjects/models/builder.model';
import { ContentSchedule } from '@/tools/models/class-builder.models';
import { SERVER_HOSTNAME, SERVER_TYPE } from '@/shared/enums/request.enum';

class BuilderServiceQueueClass {

    builderQueue : BuilderQueue<SyncBuilderDataRequest> = new BuilderQueue<SyncBuilderDataRequest>();
    
    /**
     * Save builder data on queue to sync
     * @param builderId 
     * @param builderData 
     * @returns 
     */
    async saveBuilderDataOnQueue(builderId: number, builderData: BuilderData, publishBuilder: boolean = false) {
        if(builderData) {
            this.builderQueue = new BuilderQueue<SyncBuilderDataRequest>();
            let dataToSave : SyncBuilderDataRequest = new SyncBuilderDataRequest();
            dataToSave.header = builderData.subject;
            dataToSave.builderId = builderId;
            dataToSave.specialDays = builderData.specialDays;
            dataToSave.workingDays = builderData.workingDays;
            dataToSave.contentId = builderData.contentId;
            dataToSave.templateId = builderData.templateId;
            dataToSave.contentStructure = this.readStructure(builderData.automaticStructure);
            dataToSave.translateIds = builderData.translateIds ?? true; 
            dataToSave.saveCoverImage = (builderData.coverImage) ? true : false;       
            dataToSave.publish = publishBuilder;                                                
            dataToSave.schedule = [];
            if(builderData.schedule) {
                builderData.schedule.forEach((value, key) => dataToSave.schedule.push(...value));
            }
            dataToSave.scheduleType = builderData.scheduleType;
            dataToSave.scheduleShortDay = builderData.scheduleShortDay;
            let queueData = await Vue.prototype.$BuilderCache.getFile(`sync_request_${builderId}`, FILESCACHE_KEYS.BUILDER_QUEUE);
            dataToSave.version = await this.getSyncVersion(builderData);
            if(queueData && queueData.file) {
                this.builderQueue.setQueue(queueData.file);
            }
            this.builderQueue.enqueue(dataToSave);
            await Vue.prototype.$BuilderCache.cacheFile(`sync_request_${builderId}`, this.builderQueue.elements(), FILESCACHE_KEYS.BUILDER_QUEUE);
            return dataToSave.version;
        }
    }

    private readStructure(automaticStructure : any) {
        let structureSections : StructureSections[] = [];
        let structureChanges  : StructureRequest[] = [];

        _readStructure(automaticStructure.content);
        _readStructure(automaticStructure.additional);
        _readStructure(automaticStructure.filler);
        _readStructure(automaticStructure.fun);

        return new ContentStructureRequest(structureSections, structureChanges);

        function _readStructure(section) {
            if (section.id < 0)
                structureSections.push(new StructureSections(section.id, section.type, section.name, section.text));
            if(section.parentId) // Skip root sections.  Sent all folders (not only LESSONS) to update the order
                structureChanges.push(new StructureRequest(section.id, section.parentId, section.order, section.fixedSequence, section.startDay, section.endDay, section._fixedDate));
            if(section.fixedSection === BUILDER_EVENT_TYPES.LESSON) //Stop recursion on Lessons
                return;
            if(section.sections && section.sections.length > 0)
                section.sections.forEach(sub => _readStructure(sub));
        }
    }

    /**
     * update builder data on queue to sync
     * @param builderId 
     * @param queueData 
     * @param builderCacheService 
     */
    async updateBuilderDataOnQueue(builderId : number, queueData: any[], builderCacheService: BuilderCachePlugin, publishBuilder: boolean = false, contentId: number) {
        await builderCacheService.cacheFile(`sync_request_${builderId}`, queueData, FILESCACHE_KEYS.BUILDER_QUEUE);
        await this.updateSyncBuilderStatus(builderId, BUILDER_SYNC_STATUS.DONE, builderCacheService, publishBuilder, contentId);
    }

    /**
     * save response sync service on queue to update builder data
     * @param builderId 
     * @param dataSyncServer 
     * @param builderCacheService 
     */
    async saveSyncResponseDataOnQueue(builderId : number, dataSyncServer : SyncBuilderDataResponse, builderCacheService: BuilderCachePlugin) {
        this.builderQueue = new BuilderQueue<SyncBuilderDataRequest>();
        let queueData = await builderCacheService.getFile(`sync_response_${builderId}`, FILESCACHE_KEYS.BUILDER_QUEUE);
        if(queueData && queueData.file) {
            this.builderQueue.setQueue(queueData.file);
        }
        this.builderQueue.enqueue(dataSyncServer);
        await builderCacheService.cacheFile(`sync_response_${builderId}`, this.builderQueue.elements(), FILESCACHE_KEYS.BUILDER_QUEUE);
    }

    /**
     * call sync service to save builder data on server
     * @param builderId 
     * @param syncData 
     * @param token 
     * @param builderCacheService 
     * @returns 
     */
    async syncBuilderData(builderId: number, syncData: SyncBuilderDataRequest, token, builderCacheService: BuilderCachePlugin) : Promise<any>  {
        let data = new FormData();
        
        let coverImage = null;
        if(syncData.saveCoverImage) {
            coverImage = await this.getCoverImageFromBuilder(builderId, builderCacheService);
        }

        data.append('coverImage', coverImage);
        data.append('builderRequest', JSON.stringify(syncData));
        
        const req = COURSE_API.SYNC_BUILDER_DATA.replace('{builderId}', String(builderId));
        RequestProvider.setToken(token);
        return RequestProvider.post(req, 
            data, {}, { 'Content-Type': 'multipart/form-data' });
    }

    /**
     * update sync builder status 
     * @param builderId 
     * @param status 
     * @param builderCacheService 
     */
    async updateSyncBuilderStatus(builderId : number, status: string, builderCacheService: BuilderCachePlugin, publishBuilder: boolean = false, contentId: number) {
        let builderData = await builderCacheService.getFile(`builder_${builderId}`, FILESCACHE_KEYS.BUILDER_DATA);
        if(builderData && builderData.file) {
            builderData.file.syncStatus = status;
            builderData.file.contentId = contentId;
            if(publishBuilder) {
                builderData.file.status = BUILDER_STATUS.DONE;
                builderData.file.subject.status = BUILDER_STATUS.DONE;
                builderData.file.subject.isReady = true;
            }
            await builderCacheService.cacheFile(`builder_${builderId}`, builderData.file, FILESCACHE_KEYS.BUILDER_DATA);
        }
    }

    async getSyncVersion(builderData) {
        return (builderData.version) ? ++builderData.version : 1;
    }

    /**
     * get cover image from builder data
     * @param builderId 
     * @param builderCacheService 
     * @returns 
     */
    async getCoverImageFromBuilder(builderId, builderCacheService: BuilderCachePlugin) {
        let builderData = await builderCacheService.getFile(`builder_${builderId}`, FILESCACHE_KEYS.BUILDER_DATA);
        if(builderData && builderData.file) {
            return builderData.file.coverImage;
        }
        return null;
    }

    /**
     * Save upload builder data on queue to sync
     * @param builderId 
     * @param builderData 
     * @returns 
     */
    async saveUploadBuilderDataOnQueue(builderId : number, builderData: BuilderData) {
        return await this.saveBuilderDataOnQueue(builderId, builderData, true);
    }

    /**
     * Get server status
     * @param serviceUrl 
     * @returns 
     */
    async getServerStatus(serviceUrl: string) {
      let isConnected = false;
      try {
        const hostname = process.env[SERVER_HOSTNAME[SERVER_TYPE.HEALTH]];
        const path = `${hostname}/${serviceUrl}`;
        const response = await fetch(path);
        isConnected = response.ok && (await response.json()).status === 'UP';
      } catch {
        isConnected = false;
      }
      return isConnected;
    }
}

export const BuilderServiceQueue = new BuilderServiceQueueClass();
